import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sticker, Download, Share2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";

interface StickerGeneratorProps {
  storyId: number;
  storyTitle?: string;
}

interface CharacterSticker {
  character: string;
  stickerUrl: string;
  index: number;
}

interface Character {
  name: string;
  description: string;
  stickerPrompt: string;
}

export function StickerGenerator({ storyId, storyTitle }: StickerGeneratorProps) {
  const [stickers, setStickers] = useState<CharacterSticker[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const [progress, setProgress] = useState<string>("");
  const [characters, setCharacters] = useState<Character[]>([]);

  // First step: Extract characters from the story
  const extractCharactersMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/stories/${storyId}/extract-characters`);
      return response.json();
    },
    onSuccess: async (data: any) => {
      if (data.characters && data.characters.length > 0) {
        setCharacters(data.characters);
        // Start generating stickers one by one
        generateStickersSequentially(data.characters);
      } else {
        toast({
          title: "No Characters Found",
          description: "Couldn't find any characters in the story to create stickers.",
          variant: "destructive",
        });
        setIsGenerating(false);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Character Extraction Failed",
        description: error.message || "Failed to analyze story characters. Please try again.",
        variant: "destructive",
      });
      setIsGenerating(false);
    },
  });

  // Generate stickers one by one
  const generateStickersSequentially = async (charactersToProcess: Character[]) => {
    const generatedStickers: CharacterSticker[] = [];
    
    for (let i = 0; i < charactersToProcess.length; i++) {
      const character = charactersToProcess[i];
      setProgress(`Generating sticker ${i + 1}/${charactersToProcess.length} for ${character.name}...`);
      
      try {
        const response = await apiRequest("POST", "/api/generate-single-sticker", { character }, { timeout: 30000 });
        const data = await response.json();
        
        if (data.success && data.sticker) {
          generatedStickers.push({
            ...data.sticker,
            index: i
          });
          setStickers([...generatedStickers]);
        }
      } catch (error) {
        console.error(`Failed to generate sticker for ${character.name}:`, error);
      }
    }
    
    setProgress("");
    setIsGenerating(false);
    
    if (generatedStickers.length > 0) {
      toast({
        title: "Stickers Generated!",
        description: `Created ${generatedStickers.length} character stickers from your story.`,
      });
    } else {
      toast({
        title: "Generation Failed",
        description: "Failed to generate any stickers. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setStickers([]);
    setProgress("Analyzing story characters...");
    extractCharactersMutation.mutate();
  };

  const handleDownload = async (stickerUrl: string, characterName: string) => {
    try {
      const response = await fetch(stickerUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${characterName}-sticker.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Couldn't download the sticker. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleShare = async (stickerUrl: string, characterName: string) => {
    if (navigator.share) {
      try {
        const response = await fetch(stickerUrl);
        const blob = await response.blob();
        const file = new File([blob], `${characterName}-sticker.png`, { type: 'image/png' });
        
        await navigator.share({
          title: `${characterName} Sticker`,
          text: `Check out this sticker from "${storyTitle || 'my story'}"!`,
          files: [file]
        });
      } catch (error) {
        // User cancelled or share failed
      }
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(stickerUrl);
      toast({
        title: "Link Copied!",
        description: "Sticker link copied to clipboard.",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-12"
    >
      <Card className="overflow-hidden border-2 border-purple-100">
        <CardHeader className="bg-muted">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Sticker className="w-6 h-6 text-purple-600" />
            Character Stickers
          </CardTitle>
          <CardDescription className="text-base">
            Turn story characters into fun stickers you can download and share!
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {stickers.length === 0 && !isGenerating && (
            <div className="text-center py-8">
              <div className="w-24 h-24 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-12 h-12 object-contain" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Create Character Stickers</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Generate adorable stickers of the characters from your story. Perfect for sharing or printing!
              </p>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white"
              >
                <Sticker className="mr-2 h-4 w-4" />
                Generate Stickers
              </Button>
            </div>
          )}

          {isGenerating && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-purple-600 mb-4">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="font-medium">{progress || "Analyzing story characters and creating stickers..."}</span>
              </div>
              {stickers.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {stickers.map((sticker, index) => (
                    <motion.div
                      key={`${sticker.character}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="group"
                    >
                      <div className="relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                        <div className="aspect-square p-4 bg-gradient-to-br from-purple-50 to-teal-50">
                          <img
                            src={sticker.stickerUrl}
                            alt={`${sticker.character} sticker`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="p-3 border-t">
                          <h4 className="font-semibold text-center mb-2">{sticker.character}</h4>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleDownload(sticker.stickerUrl, sticker.character)}
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleShare(sticker.stickerUrl, sticker.character)}
                            >
                              <Share2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  {/* Show skeletons for remaining stickers */}
                  {characters.length > stickers.length && Array.from({ length: characters.length - stickers.length }).map((_, i) => (
                    <div key={`skeleton-${i}`} className="space-y-2">
                      <Skeleton className="aspect-square rounded-lg" />
                      <Skeleton className="h-4 w-3/4 mx-auto" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <AnimatePresence>
            {stickers.length > 0 && !isGenerating && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {stickers.map((sticker, index) => (
                    <motion.div
                      key={`${sticker.character}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="group"
                    >
                      <div className="relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                        <div className="aspect-square p-4 bg-gradient-to-br from-purple-50 to-teal-50">
                          <img
                            src={sticker.stickerUrl}
                            alt={`${sticker.character} sticker`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="p-3 border-t">
                          <h4 className="font-semibold text-center mb-2">{sticker.character}</h4>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleDownload(sticker.stickerUrl, sticker.character)}
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleShare(sticker.stickerUrl, sticker.character)}
                            >
                              <Share2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 text-center">
                  <Button
                    variant="outline"
                    onClick={handleGenerate}
                    disabled={isGenerating}
                  >
                    <img src="/logo.png" alt="SnuggleCrafters Logo" className="mr-2 h-4 w-4 object-contain" />
                    Regenerate Stickers
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800 text-center">
                    💡 <strong>Tip:</strong> These stickers are perfect for printing on sticker paper or sharing with friends!
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}